import { Injectable } from "@angular/core";
import { EMPTY, Observable, of } from "rxjs";
import { ApicallService } from "@core-services/apicall.service";
import { parseApiResponseArray2 } from "@helpers/functions";
import { catchError, map, switchMap } from "rxjs/operators";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { Tradable } from "@models/interfaces/tradable";
import { Tradable as TradableInterface } from "@modules/+new/core/tradables/domain/entities/tradable";
import { AssetGroupName } from "@modules/+new/shared/const/enums/asset-group-name.enum";
import { StatsigClientService } from "@core-services/statsig/statsig.service";
import { isDigiAsset } from "../helpers/helper";
import { BFFSecurityAsset } from "@models/asset/bff_security_asset";

@Injectable({
	providedIn: "root"
})
export class TradeAgainService {
	constructor(
		private readonly apiCallService: ApicallService,
		private readonly tradableListService: TradableListService,
		private readonly launchDarklyService: LaunchDarklyService,
		private readonly statsigClientService: StatsigClientService
	) {}

	public getBuyAgainList$(): Observable<Tradable[]> {
		return this.launchDarklyService.isActive$(LD_FLAG.BUY_AGAIN).pipe(
			switchMap((enabled) => (enabled ? this.fetchBuyAgainList$() : EMPTY)),
			switchMap((f) => {
				return this.filterRealStockandDigiStockHelpers(f);
			}),
			map((assets) => assets.filter((cashPlus) => cashPlus.getAssetGroupName() !== AssetGroupName.FiatEarn))
		);
	}

	private fetchBuyAgainList$(): Observable<Tradable[]> {
		return this.apiCallService.get("assets/discovery/buy-again").pipe(
			map((result) => {
				// the response is a subset of Tradable class (only a few properties)
				// (we use Tradable interface for correct parsing - class won't work because of methods)
				const buyAgainList = parseApiResponseArray2<TradableInterface>(result);
				return buyAgainList
					.map((tradable) => this.tradableListService.getItemById(tradable.id))
					.filter((t) => !!t);
			}),
			catchError(() => EMPTY)
		);
	}

	public fetchMostPopularList$(): Observable<Tradable[]> {
		return this.apiCallService.get("assets/popular-saving-plans").pipe(
			map((result) => {
				const mostPopularList = parseApiResponseArray2<TradableInterface>(result);
				return mostPopularList
					.map((tradable) => this.tradableListService.getItemById(tradable.id))
					.filter((t) => !!t);
			}),
			catchError(() => EMPTY)
		);
	}

	filterRealStockandDigiStockHelpers(assets: Tradable[]): Observable<Tradable[]> {
		const filteredAssets = assets.filter((asset) => {
			const isDigi = isDigiAsset(asset?.getCurrencyType()); //Digi asset check
			const isRS = asset instanceof BFFSecurityAsset; //Real stock check
			return !isRS && !isDigi;
		});

		return of(filteredAssets);
	}
}
